import { useState, useEffect, useCallback } from 'react';
import Marketplace from './pages/Marketplace';
import BuildDetail from './pages/BuildDetail';
import RegisterBuild from './pages/RegisterBuild';
import GetKey from './pages/GetKey';
import Dashboard from './pages/Dashboard';
import ConsumerDashboard from './pages/ConsumerDashboard';
import SignIn from './pages/SignIn';
import Terms from './pages/Terms';
import BuilderDocs from './pages/BuilderDocs';
import QuickStart from './pages/QuickStart';
import BuilderFarm from './pages/BuilderFarm';
import Playground from './pages/Playground';
import Nav from './components/Nav';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';

function parseHash(hash) {
  const raw = (hash || '#marketplace').replace(/^#/, '');
  if (raw.startsWith('build/')) {
    return { page: 'build', slug: raw.slice(6) };
  }
  return { page: raw || 'marketplace', slug: null };
}

export default function App() {
  const [state, setState] = useState(() => parseHash(window.location.hash));

  const syncFromHash = useCallback(() => {
    setState(parseHash(window.location.hash));
  }, []);

  useEffect(() => {
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [syncFromHash]);

  function navigate(p, slug = null) {
    const hash = p === 'build' && slug ? `#build/${slug}` : `#${p}`;
    window.location.hash = hash;
    window.scrollTo(0, 0);
  }

  const { page, slug: selectedSlug } = state;
  const knownPages = ['marketplace','build','register','getkey','dashboard','consumer','signin','terms','privacy','refund','cookies','content','quickstart','builder-docs','builder-farm','playground'];
  const isKnown = knownPages.includes(page);

  return (
    <ErrorBoundary>
    <div>
      <Nav page={page} navigate={navigate} />
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
        {page === 'marketplace' && <Marketplace navigate={navigate} />}
        {page === 'build'       && <BuildDetail slug={selectedSlug} navigate={navigate} />}
        {page === 'register'    && <RegisterBuild navigate={navigate} />}
        {page === 'getkey'      && <GetKey navigate={navigate} />}
        {page === 'dashboard'   && <Dashboard navigate={navigate} />}
        {page === 'consumer'    && <ConsumerDashboard navigate={navigate} />}
        {page === 'signin'      && <SignIn navigate={navigate} />}
        {page === 'terms'       && <Terms navigate={navigate} />}
        {page === 'privacy'     && <Terms navigate={navigate} initialTab="privacy" />}
        {page === 'refund'      && <Terms navigate={navigate} initialTab="refund" />}
        {page === 'cookies'     && <Terms navigate={navigate} initialTab="cookies" />}
        {page === 'content'     && <Terms navigate={navigate} initialTab="content" />}
        {page === 'quickstart'   && <QuickStart navigate={navigate} />}
        {page === 'builder-docs' && <BuilderDocs navigate={navigate} />}
        {page === 'builder-farm' && <BuilderFarm navigate={navigate} />}
        {page === 'playground'   && <Playground navigate={navigate} />}
        {!isKnown && (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>404</div>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Page not found</h2>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>
              The page you're looking for doesn't exist.
            </p>
            <span style={{ color: 'var(--accent)', cursor: 'pointer', fontSize: 14 }} onClick={() => navigate('marketplace')}>
              Back to marketplace
            </span>
          </div>
        )}
      </main>
      <footer style={{ textAlign: 'center', padding: '24px 0 48px', borderTop: '1px solid var(--border)', marginTop: 64 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 0' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }} onClick={() => navigate('terms')}>
            Terms of Service
          </span>
          <span style={{ color: 'var(--muted)', margin: '0 12px' }}>·</span>
          <span style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }} onClick={() => navigate('privacy')}>
            Privacy Policy
          </span>
          <span style={{ color: 'var(--muted)', margin: '0 12px' }}>·</span>
          <span style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }} onClick={() => navigate('refund')}>
            Refund Policy
          </span>
          <span style={{ color: 'var(--muted)', margin: '0 12px' }}>·</span>
          <span style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }} onClick={() => navigate('cookies')}>
            Cookie Policy
          </span>
          <span style={{ color: 'var(--muted)', margin: '0 12px' }}>·</span>
          <span style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }} onClick={() => navigate('content')}>
            Content Policy
          </span>
          <span style={{ color: 'var(--muted)', margin: '0 12px' }}>·</span>
          <span style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }} onClick={() => navigate('builder-docs')}>
            API Docs
          </span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
          © 2026 invokeAPI
        </div>
      </footer>
    </div>
      <CookieConsent navigate={navigate} />
    </ErrorBoundary>
  );
}
